/**
 * The Studio's own reading of the URL — STUDIO_DESIGN.md 6.4.
 *
 * The generic helpers live in `utils/route-state.ts` and return raw strings; the validation
 * lives HERE, because the owner's ruling is that the two editors on these pages share leaf
 * pieces and nothing else: no shared `activeTab` union, no shared `readEditorTab`, each page
 * validating the tab string against its own set (`STUDIO_PLAN_P2/progress.md:50,52`).
 *
 * The writes 6.4 fixes, and which kind each is:
 *   open the tab     push    {tab, sid}   — Back closes the Studio
 *   leave the tab    Router.back() when the entry behind is this page's Quick convert; else replace {tab:null,…} (R13-Z1)
 *   export sheet     push    {step}       — Back closes the sheet
 *   render           replace {step, job}  — Back must not return to the sheet mid-render
 *   dead render      replace {step:null, job:null}  — S-42: nothing to resume, show the editor
 *   done             Router.push('/downloads')  (P5c owns that one)
 * Selection and edits write nothing: a history entry per click would make Back useless, so
 * there is deliberately no helper here that could produce one.
 */
import { ourEdgeHost } from '../../../utils/edge-owner';
import Router from 'next/router';

import { isValidProjectId } from '../model/composition';
import { pushQuery, readQuery, replaceQuery } from '../../../utils/route-state';

/** The Studio's tab value. The Frame picker has its own; neither knows about the other. */
export const STUDIO_TAB = 'studio';

export type StudioStep = 'edit' | 'export' | 'render';

export interface StudioUrlState {
    tab: string | null;
    sid: string | null;
    step: StudioStep;
    job: string | null;
    /**
     * IT1-W1 (saas #9): where a `?sid=` link was pressed -- `chip` for /downloads' "Back to Studio"
     * row (`STUDIO_OPENED{source:'downloads-chip'}`), absent for the card's own key and shared links.
     * Read once on arrival, never written by the editor.
     *
     * S3-01 (review 7): `new` for a link that MINTED its `sid` to open a fresh project ("Make another video" on the
     * /downloads card and banner, `newStudioHref`). Such an id has no record by construction, and the Studio used to
     * greet it with "We couldn't find that project — it may have expired or the link may be wrong."
     */
    src: 'chip' | 'new' | null;
    /**
     * S3-05 (review 7): a /downloads link asking to REBUILD this project from its workflow record when this browser has
     * none (`state/rehydrate.ts#rehydrateStudioHref`): the workflow's uuid (`rwf`) and its edge (`rdom`). Read once.
     */
    rehydrate: { workflow: string; domain: string } | null;
}

const STEPS: readonly string[] = ['edit', 'export', 'render'];

export function isStudioTab(tab: string | null): boolean {
    return tab === STUDIO_TAB;
}

/**
 * `?tab=&sid=&step=&job=` as the Studio understands them.
 *
 * An unknown `step` degrades to `edit` rather than being honoured: a crafted or stale URL
 * must not drop a user into a render screen with no render behind it.
 *
 * `sid` is checked against the schema's own `projectId` shape before it is handed on, because
 * the next thing that happens to it is a localStorage key — and `isValidProjectId` is also
 * what refuses the reserved spellings `INDEX` and `LATEST-…` that would otherwise land on
 * persistence's own bookkeeping keys (D14).
 *
 * SO A CONSUMER MUST HANDLE `sid === null` FROM A URL THAT VISIBLY CARRIES ONE. `?sid=INDEX`
 * and `?sid=not a project id` both read as `null` here, and the right response is to mint a
 * new project (or restore `readLatest(pageSlug)`), never to trust the raw parameter.
 *
 * The values are the ADDRESS BAR's, not an in-flight write's: a caller that writes and reads
 * back in the same tick sees the old URL (`utils/route-state.ts`, "READS DO NOT").
 */
export function readStudioUrl(search?: string): StudioUrlState {
    const q = readQuery(['tab', 'sid', 'step', 'job', 'src', 'rwf', 'rdom'], search);
    const step = q.step && STEPS.includes(q.step) ? (q.step as StudioStep) : 'edit';
    return {
        tab: q.tab,
        sid: isValidProjectId(q.sid) ? q.sid : null,
        step,
        job: q.job,
        src: q.src === 'chip' ? 'chip' : q.src === 'new' ? 'new' : null,
        // Review 7 SHARED-URL C-2: `rdom` names where the rebuild sends this browser's bearer -- only one of OUR edges.
        rehydrate: q.rwf && q.rdom && /^[A-Za-z0-9-]{8,64}$/.test(q.rwf) && ourEdgeHost(q.rdom) ? { workflow: q.rwf, domain: q.rdom } : null,
    };
}

/**
 * S3-01 (review 7): the one href for "open a NEW Studio project on this page" -- a fresh `sid` marked `src=new`, so
 * the Studio opens it empty and silent instead of treating an id nothing ever wrote as a lost project. For callers
 * outside the Studio (/downloads' "Make another video" card key and banner).
 */
export function newStudioHref(pageSlug: string, projectId: string): string {
    return `/${pageSlug}?tab=${STUDIO_TAB}&sid=${encodeURIComponent(projectId)}&src=new`;
}

export function openStudioTab(projectId: string): void {
    pushQuery({ tab: STUDIO_TAB, sid: projectId });
}

/**
 * Leave the Studio -- R13-Z1 (owner S1, review 5).
 *
 * Opening the tab is a PUSH (the header's table), so the entry to leave through is the one behind
 * the present. Closing with a second push -- what shipped -- left history `[Quick convert, Studio,
 * Quick convert]`, and the browser's Back from there RE-OPENED the Studio (measured on QA
 * hUixnw9H6Nk8xNfmSb_DO at 1440 and 390: "← Back to converter", then Back → `?tab=studio&sid=…`).
 * Now the entry behind the present is POPPED when it is this page's Quick convert, so Back after
 * leaving goes where the user came from; anything else -- a pasted link in a fresh tab,
 * /downloads' "Back to Studio", an unknown history -- is corrected in place with a `replace`,
 * never a push.
 *
 * WHAT IS BEHIND US is read from the Navigation API (`navigation.entries()`), which exposes the
 * URL of every same-origin entry in this tab; a browser without it gets the `replace`. NOT from
 * `history.state.options.shallow`: Next writes that for every shallow write, and this editor's
 * own `replaceQuery({sid})` stamps it onto a pasted deep link too -- measured on dev, the exit key
 * then walked off the site (`about:blank`).
 *
 * A present entry on the export step (`?step=export`, pushed by `pushExportStep`) is corrected in
 * place as well: one `Router.back()` would land on the editor, not on Quick convert, and the page
 * would paint Quick convert for a frame before `syncTabFromUrl` put the Studio back.
 */
export function closeStudioTab(): void {
    if (backLandsOnQuickConvert()) {
        void Router.back();
        return;
    }
    replaceQuery({ tab: null, sid: null, step: null, job: null, src: null });
}

/** The entry behind the present one is THIS page with no editor open, and the present one is the editor. */
export function backLandsOnQuickConvert(): boolean {
    if (typeof window === 'undefined') return false;
    const nav = (window as unknown as { navigation?: NavigationLike }).navigation;
    if (!nav || !nav.currentEntry || typeof nav.entries !== 'function') return false;
    const index = nav.currentEntry.index;
    if (!(index > 0)) return false;
    const prev = nav.entries()[index - 1];
    // A cross-origin entry reads `url: null`.
    if (!prev || !prev.url) return false;
    let url: URL;
    try { url = new URL(prev.url); } catch { return false; }
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return false;
    if (url.searchParams.get('tab') !== null) return false;
    return readStudioUrl().step === 'edit';
}

/** The slice of the Navigation API this module reads (no lib.dom typing for it in this tree yet). */
interface NavigationLike {
    currentEntry: { index: number } | null;
    entries: () => Array<{ url: string | null }>;
}

export function pushExportStep(): void {
    pushQuery({ step: 'export' });
}

/** `replace`, so Back from a running render does not return to the sheet that started it. */
export function replaceRenderStep(job: string): void {
    replaceQuery({ step: 'render', job });
}

/**
 * IT1-W1 (qa #10): the render step is written the moment the card exists -- BEFORE the schedule call
 * is answered -- so a reload mid-schedule lands on the panel that follows the card, not on a form
 * that would submit the same project again. A refusal or a failure then puts the FORM back in the
 * URL with a `replace` (the entry is the one `pushExportStep` pushed, corrected in place).
 */
export function replaceExportStep(): void {
    replaceQuery({ step: 'export', job: null });
}

/**
 * `replace` the render step AWAY — S-42, the fifth write.
 *
 * Used when a `?step=render&job=…` arrival finds no render to resume (the card finished, failed
 * or belongs to another browser), and when the export step is left without an entry of this
 * editor's own to pop. A `replace` and never a `push`: the entry being corrected IS the current
 * one, and Back from the editor must still leave the Studio, not return to the dead sheet.
 */
export function clearRenderStep(): void {
    replaceQuery({ step: null, job: null });
}
